import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Box, Stack, Typography, Divider } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { IEmailLogContentDTO, amplitude, ICandidate, getEmailLogsForCandidate } from 'wisestep-api';
import {
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsColors,
  activityFormattedDate,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';

interface IEmailLogs {
  candidate: ICandidate;
  amplitudeEventHandler?: (eventName: string) => void;
}

const EmailLogs = ({ candidate, amplitudeEventHandler = () => {} }: IEmailLogs) => {
  const [emailLogsData, setEmailLogsData] = useState<IEmailLogContentDTO[]>([]);

  useEffect(() => {
    const getEmailLogs = async () => {
      const response = await getEmailLogsForCandidate(candidate?.candidateId ?? '');
      if (response.data) {
        const logs = mapEmailLogsToDTO(response.data);
        setEmailLogsData(logs);
      }
    };
    getEmailLogs();

    amplitude('CandidateView_Communication_Logs', {
      clickedOn: 'Communication Logs Email',
      pageName: 'candidate-layout',
      pageUrl: window.location.href,
    });
    amplitudeEventHandler('Communication Logs Email');
  }, [candidate]);

  const mapEmailLogsToDTO = (emailLogs: any[]): IEmailLogContentDTO[] => {
    return emailLogs.map(
      (emailLog): IEmailLogContentDTO => ({
        id: emailLog.id,
        recruiterId: emailLog.recruiterId,
        replyTo: emailLog.replyTo,
        subject: emailLog.subject,
        message: emailLog.body ?? 'NA',
        createdOn: new Date(emailLog.createdOn).toISOString(),
      })
    );
  };

  return (
    <>
      {!isEmpty(emailLogsData) && (
        <Box bgcolor="White">
          <Timeline
            sx={{
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {emailLogsData?.map((emailLog, index: number) => (
              <>
                <TimelineItem key={index}>
                  <TimelineSeparator>
                    <TimelineDot variant="outlined" color="primary" />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Stack direction="column" spacing={1.5}>
                      <WsTypography>{`${activityFormattedDate(
                        String(emailLog?.createdOn ?? '')
                      )}`}</WsTypography>
                      <WsTypography
                        variant={WsTypoVariants.TITLE}
                        bold={true}
                        color={WsTypoColors.SENARY}
                      >
                        {`Subject: ${emailLog?.subject}`}
                      </WsTypography>
                    </Stack>
                    <Typography
                      variant={WsTypoVariants.SUB_TITLE}
                      color={WsColors.TertiaryShades800}
                      dangerouslySetInnerHTML={{ __html: emailLog?.message ?? 'NA' }}
                    />
                    <br></br>
                    <Stack direction="row" spacing={1.5}>
                      <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.SENARY}>
                        Sent by
                      </WsTypography>
                      <WsTypography
                        variant={WsTypoVariants.TITLE}
                        bold={true}
                        color={WsTypoColors.SENARY}
                      >
                        {` ${emailLog?.recruiterName}`}
                      </WsTypography>
                    </Stack>
                  </TimelineContent>
                </TimelineItem>
                <Divider sx={{ mt: 1 }} />
              </>
            ))}
          </Timeline>
        </Box>
      )}
      {isEmpty(emailLogsData) && <NoDataFoundCard text="No Email Logs Found" />}
    </>
  );
};

export default EmailLogs;
